import React, { useState, useEffect, useRef } from "react";
import { useParams } from "react-router-dom";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "./ui/select";
import { Textarea } from "./ui/textarea";
import { CheckCircle2, AlertCircle, Camera, X } from "lucide-react";
import { toast } from "sonner";
import SignatureCanvas from "react-signature-canvas";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Phone number formatter: #### ### ###
const formatPhoneNumber = (value) => {
  // Remove all non-digit characters
  const digits = value.replace(/\D/g, '');
  
  // Limit to 10 digits
  const limited = digits.slice(0, 10);
  
  // Format as #### ### ###
  if (limited.length <= 4) {
    return limited;
  } else if (limited.length <= 7) {
    return `${limited.slice(0, 4)} ${limited.slice(4)}`;
  } else {
    return `${limited.slice(0, 4)} ${limited.slice(4, 7)} ${limited.slice(7)}`;
  }
};

// Helper function to get local date in YYYY-MM-DD format
const getLocalDateString = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export default function SiteInductionForm() {
  const { siteId } = useParams();
  const [siteName, setSiteName] = useState("");
  const [inductionNumber, setInductionNumber] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [companies, setCompanies] = useState([]);
  const signatureRef = useRef(null);
  const managerSignatureRef = useRef(null);

  const [formData, setFormData] = useState({
    site_id: siteId,
    company_name: "",
    occupation: "",
    employment_status: "Full-time",
    first_name: "",
    surname: "",
    date_of_birth: "",
    contact_number: "",
    emergency_contact_name: "",
    emergency_relationship: "",
    emergency_phone: "",
    emergency_mobile: "",
    pre_existing_injury: false,
    pre_existing_injury_details: "",
    medication_allergy: false,
    medication_allergy_details: "",
    identification_type: "",
    industry_induction_card_number: "",
    high_risk_license_type: "",
    high_risk_card_number: "",
    high_risk_classes: "",
    high_risk_expiry: "",
    other_competencies: "",
    induction_explained: true,
    induction_explained_explanation: "",
    understand_induction: true,
    understand_induction_explanation: "",
    agree_to_comply: true,
    agree_to_comply_explanation: "",
    opportunity_for_questions: true,
    opportunity_for_questions_explanation: "",
    employer_communicated_swms: true,
    employer_communicated_swms_explanation: "",
    inductee_signature: "",
    inductee_signature_date: getLocalDateString(),
    inductor_name: "",
    inductor_signature: "",
    inductor_signature_date: getLocalDateString(),
    // Photo fields
    industry_induction_card_photo: "",
    identification_photo: "",
    high_risk_license_photo: "",
    other_competencies_photo: "",
  });

  // Load site info and pre-populate site manager name
  useEffect(() => {
    const loadSite = async () => {
      try {
        console.log('🔍 Loading site data for siteId:', siteId);
        const response = await fetch(`${API}/public/sites/${siteId}`);
        console.log('🔍 Site API response status:', response.status);
        
        if (response.ok) {
          const data = await response.json();
          console.log('🔍 Site data received:', data);
          console.log('🔍 site_manager_names:', data.site_manager_names);
          console.log('🔍 site_manager_name:', data.site_manager_name);
          
          setSiteName(data.name);
          
          // Generate temporary induction number for display
          const today = new Date();
          const dateStr = today.toISOString().split('T')[0].replace(/-/g, '');
          const tempNumber = `IND-${dateStr}-TEMP`;
          setInductionNumber(tempNumber);
          
          // Pre-populate site manager name
          if (data.site_manager_names && data.site_manager_names.length > 0) {
            // Use the first manager if multiple are assigned
            const managerName = data.site_manager_names[0];
            console.log('✅ Setting inductor_name to:', managerName);
            setFormData(prev => ({ ...prev, inductor_name: managerName }));
          } else if (data.site_manager_name) {
            // Fallback to old field for backward compatibility
            console.log('✅ Setting inductor_name to (fallback):', data.site_manager_name);
            setFormData(prev => ({ ...prev, inductor_name: data.site_manager_name }));
          } else {
            console.log('⚠️ No site manager found for this site');
          }
        } else {
          console.error('❌ Failed to load site, status:', response.status);
        }
      } catch (error) {
        console.error("❌ Error loading site:", error);
      }
    };

    if (siteId) {
      loadSite();
    }
  }, [siteId]);

  // Load companies
  useEffect(() => {
    const loadCompanies = async () => {
      if (!siteId) return;
      
      try {
        // Load companies that have attended this specific site
        console.log("Loading companies for site:", siteId);
        const response = await fetch(`${API}/ohs/site-companies/${siteId}`);
        if (response.ok) {
          const data = await response.json();
          console.log("Companies loaded:", data.length, "companies");
          console.log("First company sample:", data[0]);
          setCompanies(data);
        } else {
          console.error("Failed to load companies, status:", response.status);
        }
      } catch (error) {
        console.error("Error loading companies:", error);
      }
    };

    loadCompanies();
  }, [siteId]);

  const clearSignature = () => {
    signatureRef.current?.clear();
    setFormData(prev => ({ ...prev, inductee_signature: "" }));
  };

  const clearManagerSignature = () => {
    managerSignatureRef.current?.clear();
    setFormData(prev => ({ ...prev, inductor_signature: "" }));
  };

  // Capture each signature into state as soon as a stroke ends. This is critical:
  // react-signature-canvas wipes the canvas pixels on any window 'resize' event
  // (mobile browsers fire 'resize' when the address bar shows/hides on scroll), so
  // relying on the live canvas at submit time loses the signature. State survives.
  const captureInducteeSignature = () => {
    if (signatureRef.current && !signatureRef.current.isEmpty()) {
      setFormData(prev => ({ ...prev, inductee_signature: signatureRef.current.toDataURL() }));
    }
  };

  const captureManagerSignature = () => {
    if (managerSignatureRef.current && !managerSignatureRef.current.isEmpty()) {
      setFormData(prev => ({ ...prev, inductor_signature: managerSignatureRef.current.toDataURL() }));
    }
  };

  // After a resize (which clears the canvases), redraw the saved signatures so the
  // user still sees them and the data stays intact.
  useEffect(() => {
    const restoreSignatures = () => {
      setTimeout(() => {
        if (formData.inductee_signature && signatureRef.current?.isEmpty()) {
          signatureRef.current.fromDataURL(formData.inductee_signature);
        }
        if (formData.inductor_signature && managerSignatureRef.current?.isEmpty()) {
          managerSignatureRef.current.fromDataURL(formData.inductor_signature);
        }
      }, 150);
    };
    window.addEventListener("resize", restoreSignatures);
    window.addEventListener("orientationchange", restoreSignatures);
    return () => {
      window.removeEventListener("resize", restoreSignatures);
      window.removeEventListener("orientationchange", restoreSignatures);
    };
  }, [formData.inductee_signature, formData.inductor_signature]);

  const handlePhotoCapture = (fieldName, event) => {
    const file = event.target.files[0];
    if (file) {
      // Check file size (max 5MB)
      if (file.size > 5 * 1024 * 1024) {
        toast.error("Image size must be less than 5MB");
        return;
      }

      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData(prev => ({ ...prev, [fieldName]: reader.result }));
      };
      reader.readAsDataURL(file);
    }
  };

  const removePhoto = (fieldName) => {
    setFormData(prev => ({ ...prev, [fieldName]: "" }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate inductee signature (use saved state — the live canvas may have been
    // wiped by a mobile resize/scroll, but the state preserves the captured signature)
    const inducteeSig = formData.inductee_signature || (signatureRef.current && !signatureRef.current.isEmpty() ? signatureRef.current.toDataURL() : "");
    if (!inducteeSig) {
      toast.error("Personnel signature is required");
      return;
    }

    // Validate site manager signature
    const managerSig = formData.inductor_signature || (managerSignatureRef.current && !managerSignatureRef.current.isEmpty() ? managerSignatureRef.current.toDataURL() : "");
    if (!managerSig) {
      toast.error("Site Manager signature is required");
      return;
    }

    // Validate manager name
    if (!formData.inductor_name || formData.inductor_name.trim() === "") {
      toast.error("Site Manager name is required");
      return;
    }

    // Validate pre-existing injury details if Yes is selected
    if (formData.pre_existing_injury === true && !formData.pre_existing_injury_details.trim()) {
      toast.error("Please provide details about your pre-existing injury or illness");
      return;
    }

    // Validate medication allergy details if Yes is selected
    if (formData.medication_allergy === true && !formData.medication_allergy_details.trim()) {
      toast.error("Please list the medications you are allergic to");
      return;
    }

    // Validate Industry Induction Card (mandatory - need either text or photo)
    if (!formData.industry_induction_card_number && !formData.industry_induction_card_photo) {
      toast.error("Industry Induction Card No. is required (provide card number or photo)");
      return;
    }

    // Validate all declaration checkboxes
    if (!formData.induction_explained || !formData.understand_induction || 
        !formData.agree_to_comply || !formData.opportunity_for_questions || 
        !formData.employer_communicated_swms) {
      toast.error("Please answer all declaration questions");
      return;
    }

    // Validate explanation if any declaration is "No"
    if (formData.induction_explained === false && !formData.induction_explained_explanation.trim()) {
      toast.error("Please explain why the induction was not explained");
      return;
    }
    if (formData.understand_induction === false && !formData.understand_induction_explanation.trim()) {
      toast.error("Please explain why you don't understand the induction");
      return;
    }
    if (formData.agree_to_comply === false && !formData.agree_to_comply_explanation.trim()) {
      toast.error("Please explain why you don't agree to comply");
      return;
    }
    if (formData.opportunity_for_questions === false && !formData.opportunity_for_questions_explanation.trim()) {
      toast.error("Please explain why you weren't given the opportunity to ask questions");
      return;
    }
    if (formData.employer_communicated_swms === false && !formData.employer_communicated_swms_explanation.trim()) {
      toast.error("Please explain why your employer hasn't communicated SWMS");
      return;
    }

    setLoading(true);

    try {
      // Use the signatures captured into state (resize-safe), falling back to the
      // live canvas if somehow not captured yet.
      const signatureData = inducteeSig;
      const managerSignatureData = managerSig;
      
      const submitData = {
        ...formData,
        inductee_signature: signatureData,
        inductor_signature: managerSignatureData,
      };
      
      console.log("Submitting induction with company_name:", submitData.company_name);
      
      const response = await fetch(`${API}/ohs/inductions/check-in`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(submitData),
      });

      if (response.ok) {
        const result = await response.json();
        // Update with actual induction number from backend
        if (result.induction_number) {
          setInductionNumber(result.induction_number);
        }
        setSubmitted(true);
        toast.success("Induction submitted successfully!");
        
        // Close window after 3 seconds
        setTimeout(() => {
          window.close();
        }, 3000);
      } else {
        const error = await response.json();
        toast.error(error.detail || "Failed to submit induction");
      }
    } catch (error) {
      console.error("Error submitting induction:", error);
      toast.error("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 p-4">
        <Card className="w-full max-w-md">
          <CardContent className="pt-8 pb-8">
            <div className="text-center space-y-4">
              <CheckCircle2 className="w-20 h-20 text-green-600 mx-auto" />
              <h2 className="text-2xl font-bold text-slate-800">Induction Submitted!</h2>
              <p className="text-slate-600">The site induction has been successfully recorded.</p>
              <p className="text-sm text-slate-500">
                Daily attendance has been automatically created for this person.
              </p>
              <p className="text-xs text-slate-400 mt-4">This window will close automatically...</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-3 sm:p-4 md:p-6">
      <div className="max-w-4xl mx-auto">
        <Card>
          <CardHeader className="bg-gradient-to-r from-blue-600 to-indigo-600 text-white p-4 sm:p-6">
            <div className="flex flex-col sm:flex-row items-start justify-between gap-3">
              <div className="flex items-center space-x-3 w-full sm:w-auto">
                <img src="/logo.png?v=3" alt="DCS Built" className="w-10 h-10 sm:w-12 sm:h-12" />
                <div className="flex-1">
                  <CardTitle className="text-xl sm:text-2xl">Site Induction Form</CardTitle>
                  <p className="text-sm sm:text-lg font-semibold opacity-100 mt-1">
                    {siteName ? `Site: ${siteName}` : "Loading site..."}
                  </p>
                </div>
              </div>
              {inductionNumber && (
                <div className="text-left sm:text-right">
                  <p className="text-xs opacity-80">Induction Number</p>
                  <p className="text-base sm:text-lg font-bold">{inductionNumber}</p>
                </div>
              )}
            </div>
          </CardHeader>

          <CardContent className="pt-4 sm:pt-6 px-4 sm:px-6">
            <form onSubmit={handleSubmit} className="space-y-6 sm:space-y-8">
              {/* Personal Details */}
              <div className="space-y-3 sm:space-y-4">
                <h3 className="text-base sm:text-lg font-semibold text-slate-800 border-b pb-2">Personal Details</h3>
                
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                  <div>
                    <Label htmlFor="company_name">Company Name *</Label>
                    <Select
                      value={formData.company_name}
                      onValueChange={(value) => {
                        console.log("Company selected:", value);
                        setFormData(prev => ({ ...prev, company_name: value }));
                      }}
                      required
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select your company" />
                      </SelectTrigger>
                      <SelectContent>
                        {companies.map((company) => (
                          <SelectItem key={company.id} value={company.name}>
                            {company.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-xs text-amber-700 mt-2 font-medium">
                      ⚠️ If your company is not listed, please see the Site Manager to create the subcontractor attendance for the site.
                    </p>
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="occupation">Occupation *</Label>
                      <Input
                        id="occupation"
                        value={formData.occupation}
                        onChange={(e) => setFormData(prev => ({ ...prev, occupation: e.target.value }))}
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="employment_status">Employment Status *</Label>
                      <Select
                        value={formData.employment_status}
                        onValueChange={(value) => setFormData(prev => ({ ...prev, employment_status: value }))}
                      >
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="Full-time">Full-time</SelectItem>
                          <SelectItem value="Part-time">Part-time</SelectItem>
                          <SelectItem value="Casual">Casual</SelectItem>
                          <SelectItem value="Self-employed">Self-employed</SelectItem>
                          <SelectItem value="Contractor">Contractor</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="first_name">First Name *</Label>
                    <Input
                      id="first_name"
                      value={formData.first_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, first_name: e.target.value }))}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="surname">Surname *</Label>
                    <Input
                      id="surname"
                      value={formData.surname}
                      onChange={(e) => setFormData(prev => ({ ...prev, surname: e.target.value }))}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="date_of_birth">Date of Birth *</Label>
                    <Input
                      id="date_of_birth"
                      type="date"
                      value={formData.date_of_birth}
                      onChange={(e) => setFormData(prev => ({ ...prev, date_of_birth: e.target.value }))}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="contact_number">Contact Number *</Label>
                    <Input
                      id="contact_number"
                      type="tel"
                      placeholder="0412 345 678"
                      value={formData.contact_number}
                      onChange={(e) => {
                        const formatted = formatPhoneNumber(e.target.value);
                        setFormData(prev => ({ ...prev, contact_number: formatted }));
                      }}
                      required
                      maxLength={12}
                    />
                    <p className="text-xs text-slate-500 mt-1">Format: #### ### ###</p>
                  </div>
                </div>
              </div>

              {/* Emergency Details */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-800 border-b pb-2">Emergency Contact Details</h3>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="emergency_contact_name">Emergency Contact Name *</Label>
                    <Input
                      id="emergency_contact_name"
                      value={formData.emergency_contact_name}
                      onChange={(e) => setFormData(prev => ({ ...prev, emergency_contact_name: e.target.value }))}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="emergency_relationship">Relationship *</Label>
                    <Input
                      id="emergency_relationship"
                      placeholder="e.g., Spouse, Parent, Sibling"
                      value={formData.emergency_relationship}
                      onChange={(e) => setFormData(prev => ({ ...prev, emergency_relationship: e.target.value }))}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="emergency_phone">Emergency Phone *</Label>
                    <Input
                      id="emergency_phone"
                      type="tel"
                      placeholder="0412 345 678"
                      value={formData.emergency_phone}
                      onChange={(e) => {
                        const formatted = formatPhoneNumber(e.target.value);
                        setFormData(prev => ({ ...prev, emergency_phone: formatted }));
                      }}
                      required
                      maxLength={12}
                    />
                    <p className="text-xs text-slate-500 mt-1">Format: #### ### ###</p>
                  </div>
                </div>

                <div className="space-y-4 p-4 bg-amber-50 rounded-lg border border-amber-200">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">
                      Is there any pre-existing injury or illness which may be affected by your duties? *
                    </label>
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2">
                        <input
                          type="radio"
                          id="pre_existing_injury_yes"
                          name="pre_existing_injury"
                          checked={formData.pre_existing_injury === true}
                          onChange={() => setFormData(prev => ({ ...prev, pre_existing_injury: true }))}
                          className="w-4 h-4"
                          required
                        />
                        <label htmlFor="pre_existing_injury_yes" className="text-sm cursor-pointer">
                          Yes
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="radio"
                          id="pre_existing_injury_no"
                          name="pre_existing_injury"
                          checked={formData.pre_existing_injury === false}
                          onChange={() => setFormData(prev => ({ ...prev, pre_existing_injury: false, pre_existing_injury_details: "" }))}
                          className="w-4 h-4"
                        />
                        <label htmlFor="pre_existing_injury_no" className="text-sm cursor-pointer">
                          No
                        </label>
                      </div>
                    </div>
                    {formData.pre_existing_injury === true && (
                      <Textarea
                        className="mt-2"
                        placeholder="Please provide details..."
                        value={formData.pre_existing_injury_details}
                        onChange={(e) => setFormData(prev => ({ ...prev, pre_existing_injury_details: e.target.value }))}
                        required
                      />
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">
                      Are you allergic to any medication that you are aware of? *
                    </label>
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2">
                        <input
                          type="radio"
                          id="medication_allergy_yes"
                          name="medication_allergy"
                          checked={formData.medication_allergy === true}
                          onChange={() => setFormData(prev => ({ ...prev, medication_allergy: true }))}
                          className="w-4 h-4"
                          required
                        />
                        <label htmlFor="medication_allergy_yes" className="text-sm cursor-pointer">
                          Yes
                        </label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="radio"
                          id="medication_allergy_no"
                          name="medication_allergy"
                          checked={formData.medication_allergy === false}
                          onChange={() => setFormData(prev => ({ ...prev, medication_allergy: false, medication_allergy_details: "" }))}
                          className="w-4 h-4"
                        />
                        <label htmlFor="medication_allergy_no" className="text-sm cursor-pointer">
                          No
                        </label>
                      </div>
                    </div>
                    {formData.medication_allergy === true && (
                      <Textarea
                        className="mt-2"
                        placeholder="Please list medications..."
                        value={formData.medication_allergy_details}
                        onChange={(e) => setFormData(prev => ({ ...prev, medication_allergy_details: e.target.value }))}
                        required
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* Licenses & Competencies */}
              <div className="space-y-3 sm:space-y-4">
                <h3 className="text-base sm:text-lg font-semibold text-slate-800 border-b pb-2">Licenses, Tickets & Competencies</h3>
                
                <div className="space-y-2">
                  <Label htmlFor="industry_induction_card_number">
                    Industry Induction Card No. *
                    <span className="text-xs text-slate-500 ml-2">(Card number or photo required)</span>
                  </Label>
                  <Input
                    id="industry_induction_card_number"
                    placeholder="Card number"
                    value={formData.industry_induction_card_number}
                    onChange={(e) => setFormData(prev => ({ ...prev, industry_induction_card_number: e.target.value }))}
                  />
                  <div className="flex items-center space-x-2 mt-2">
                    <Input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={(e) => handlePhotoCapture('industry_induction_card_photo', e)}
                      className="hidden"
                      id="industry_card_photo"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => document.getElementById('industry_card_photo').click()}
                    >
                      <Camera className="w-4 h-4 mr-2" />
                      {formData.industry_induction_card_photo ? "Change Photo" : "Take Photo"}
                    </Button>
                    {formData.industry_induction_card_photo && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removePhoto('industry_induction_card_photo')}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  {formData.industry_induction_card_photo && (
                    <div className="mt-2 border rounded p-2 bg-slate-50">
                      <img 
                        src={formData.industry_induction_card_photo} 
                        alt="Industry Induction Card" 
                        className="max-h-40 mx-auto rounded"
                      />
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="identification_type">
                    Identification
                    <span className="text-xs text-slate-500 ml-2">(Optional - provide description or photo or both)</span>
                  </Label>
                  <Input
                    id="identification_type"
                    placeholder="Driver License, Passport, etc."
                    value={formData.identification_type}
                    onChange={(e) => setFormData(prev => ({ ...prev, identification_type: e.target.value }))}
                  />
                  <div className="flex items-center space-x-2 mt-2">
                    <Input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={(e) => handlePhotoCapture('identification_photo', e)}
                      className="hidden"
                      id="identification_photo_input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => document.getElementById('identification_photo_input').click()}
                    >
                      <Camera className="w-4 h-4 mr-2" />
                      {formData.identification_photo ? "Change Photo" : "Take Photo"}
                    </Button>
                    {formData.identification_photo && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removePhoto('identification_photo')}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  {formData.identification_photo && (
                    <div className="mt-2 border rounded p-2 bg-slate-50">
                      <img 
                        src={formData.identification_photo} 
                        alt="Identification" 
                        className="max-h-40 mx-auto rounded"
                      />
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="high_risk_license_type">
                    License to Perform High Risk Work
                    <span className="text-xs text-slate-500 ml-2">(Optional - provide license type or photo or both)</span>
                  </Label>
                  <Input
                    id="high_risk_license_type"
                    placeholder="License type"
                    value={formData.high_risk_license_type}
                    onChange={(e) => setFormData(prev => ({ ...prev, high_risk_license_type: e.target.value }))}
                  />
                  <div className="flex items-center space-x-2 mt-2">
                    <Input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={(e) => handlePhotoCapture('high_risk_license_photo', e)}
                      className="hidden"
                      id="high_risk_photo_input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => document.getElementById('high_risk_photo_input').click()}
                    >
                      <Camera className="w-4 h-4 mr-2" />
                      {formData.high_risk_license_photo ? "Change Photo" : "Take Photo"}
                    </Button>
                    {formData.high_risk_license_photo && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removePhoto('high_risk_license_photo')}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  {formData.high_risk_license_photo && (
                    <div className="mt-2 border rounded p-2 bg-slate-50">
                      <img 
                        src={formData.high_risk_license_photo} 
                        alt="High Risk License" 
                        className="max-h-40 mx-auto rounded"
                      />
                    </div>
                  )}
                </div>

                <div className="space-y-2">
                  <Label htmlFor="other_competencies">
                    Other Competencies
                    <span className="text-xs text-slate-500 ml-2">(Optional - provide description or photo or both)</span>
                  </Label>
                  <Textarea
                    id="other_competencies"
                    placeholder="List any other certifications, tickets, or competencies..."
                    value={formData.other_competencies}
                    onChange={(e) => setFormData(prev => ({ ...prev, other_competencies: e.target.value }))}
                    rows={3}
                  />
                  <div className="flex items-center space-x-2 mt-2">
                    <Input
                      type="file"
                      accept="image/*"
                      capture="environment"
                      onChange={(e) => handlePhotoCapture('other_competencies_photo', e)}
                      className="hidden"
                      id="other_competencies_photo_input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => document.getElementById('other_competencies_photo_input').click()}
                    >
                      <Camera className="w-4 h-4 mr-2" />
                      {formData.other_competencies_photo ? "Change Photo" : "Take Photo"}
                    </Button>
                    {formData.other_competencies_photo && (
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removePhoto('other_competencies_photo')}
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    )}
                  </div>
                  {formData.other_competencies_photo && (
                    <div className="mt-2 border rounded p-2 bg-slate-50">
                      <img 
                        src={formData.other_competencies_photo} 
                        alt="Other Competencies" 
                        className="max-h-40 mx-auto rounded"
                      />
                    </div>
                  )}
                </div>
              </div>

              {/* NOTE Section */}
              <div className="p-6 bg-amber-50 border-2 border-amber-300 rounded-lg">
                <h3 className="text-lg font-bold text-amber-900 mb-3">NOTE</h3>
                <p className="text-sm text-slate-700 leading-relaxed">
                  Project employees and contractors are expected to abide by company policy and engage in the highest ethical standards with 
                  each other, with customers, suppliers, contractors, and the community. This includes participating in appropriate dress 
                  standards, hygiene, communication, and courteous and respectful behavior. It also means accepting and appreciating that we 
                  are all different.
                </p>
              </div>

              {/* Inductee Declaration */}
              <div className="space-y-3 sm:space-y-4">
                <h3 className="text-base sm:text-lg font-semibold text-slate-800 border-b pb-2">Inductee Declaration</h3>
                <p className="text-xs sm:text-sm text-slate-600">To be completed at the end of the induction</p>
                
                <div className="space-y-3 sm:space-y-4 p-3 sm:p-4 bg-blue-50 rounded-lg border border-blue-200">
                  <div className="space-y-2">
                    <label className="text-sm font-medium">
                      Were all parts of this safety induction explained to you and the induction booklet made available? *
                    </label>
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2">
                        <input
                          type="radio"
                          id="induction_explained_yes"
                          name="induction_explained"
                          checked={formData.induction_explained === true}
                          onChange={() => setFormData(prev => ({ ...prev, induction_explained: true, induction_explained_explanation: "" }))}
                          className="w-4 h-4"
                          required
                        />
                        <label htmlFor="induction_explained_yes" className="text-sm cursor-pointer">Yes</label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="radio"
                          id="induction_explained_no"
                          name="induction_explained"
                          checked={formData.induction_explained === false}
                          onChange={() => setFormData(prev => ({ ...prev, induction_explained: false }))}
                          className="w-4 h-4"
                        />
                        <label htmlFor="induction_explained_no" className="text-sm cursor-pointer">No</label>
                      </div>
                    </div>
                    {formData.induction_explained === false && (
                      <Textarea
                        className="mt-2"
                        placeholder="Please explain..."
                        value={formData.induction_explained_explanation}
                        onChange={(e) => setFormData(prev => ({ ...prev, induction_explained_explanation: e.target.value }))}
                        required
                      />
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">
                      Do you understand all parts of this safety induction and the contents of the induction booklet? *
                    </label>
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2">
                        <input
                          type="radio"
                          id="understand_induction_yes"
                          name="understand_induction"
                          checked={formData.understand_induction === true}
                          onChange={() => setFormData(prev => ({ ...prev, understand_induction: true, understand_induction_explanation: "" }))}
                          className="w-4 h-4"
                          required
                        />
                        <label htmlFor="understand_induction_yes" className="text-sm cursor-pointer">Yes</label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="radio"
                          id="understand_induction_no"
                          name="understand_induction"
                          checked={formData.understand_induction === false}
                          onChange={() => setFormData(prev => ({ ...prev, understand_induction: false }))}
                          className="w-4 h-4"
                        />
                        <label htmlFor="understand_induction_no" className="text-sm cursor-pointer">No</label>
                      </div>
                    </div>
                    {formData.understand_induction === false && (
                      <Textarea
                        className="mt-2"
                        placeholder="Please explain..."
                        value={formData.understand_induction_explanation}
                        onChange={(e) => setFormData(prev => ({ ...prev, understand_induction_explanation: e.target.value }))}
                        required
                      />
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">
                      Do you agree to comply with all site rules and conditions and obligations as stated? *
                    </label>
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2">
                        <input
                          type="radio"
                          id="agree_to_comply_yes"
                          name="agree_to_comply"
                          checked={formData.agree_to_comply === true}
                          onChange={() => setFormData(prev => ({ ...prev, agree_to_comply: true, agree_to_comply_explanation: "" }))}
                          className="w-4 h-4"
                          required
                        />
                        <label htmlFor="agree_to_comply_yes" className="text-sm cursor-pointer">Yes</label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="radio"
                          id="agree_to_comply_no"
                          name="agree_to_comply"
                          checked={formData.agree_to_comply === false}
                          onChange={() => setFormData(prev => ({ ...prev, agree_to_comply: false }))}
                          className="w-4 h-4"
                        />
                        <label htmlFor="agree_to_comply_no" className="text-sm cursor-pointer">No</label>
                      </div>
                    </div>
                    {formData.agree_to_comply === false && (
                      <Textarea
                        className="mt-2"
                        placeholder="Please explain..."
                        value={formData.agree_to_comply_explanation}
                        onChange={(e) => setFormData(prev => ({ ...prev, agree_to_comply_explanation: e.target.value }))}
                        required
                      />
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">
                      Were you given the opportunity to ask, and have questions answered? *
                    </label>
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2">
                        <input
                          type="radio"
                          id="opportunity_for_questions_yes"
                          name="opportunity_for_questions"
                          checked={formData.opportunity_for_questions === true}
                          onChange={() => setFormData(prev => ({ ...prev, opportunity_for_questions: true, opportunity_for_questions_explanation: "" }))}
                          className="w-4 h-4"
                          required
                        />
                        <label htmlFor="opportunity_for_questions_yes" className="text-sm cursor-pointer">Yes</label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="radio"
                          id="opportunity_for_questions_no"
                          name="opportunity_for_questions"
                          checked={formData.opportunity_for_questions === false}
                          onChange={() => setFormData(prev => ({ ...prev, opportunity_for_questions: false }))}
                          className="w-4 h-4"
                        />
                        <label htmlFor="opportunity_for_questions_no" className="text-sm cursor-pointer">No</label>
                      </div>
                    </div>
                    {formData.opportunity_for_questions === false && (
                      <Textarea
                        className="mt-2"
                        placeholder="Please explain..."
                        value={formData.opportunity_for_questions_explanation}
                        onChange={(e) => setFormData(prev => ({ ...prev, opportunity_for_questions_explanation: e.target.value }))}
                        required
                      />
                    )}
                  </div>

                  <div className="space-y-2">
                    <label className="text-sm font-medium">
                      Has your employer communicated to you, your company's safe operating procedures inclusive of your Safe Work Method Statements (SWMS)? *
                    </label>
                    <div className="flex items-center space-x-4">
                      <div className="flex items-center space-x-2">
                        <input
                          type="radio"
                          id="employer_communicated_swms_yes"
                          name="employer_communicated_swms"
                          checked={formData.employer_communicated_swms === true}
                          onChange={() => setFormData(prev => ({ ...prev, employer_communicated_swms: true, employer_communicated_swms_explanation: "" }))}
                          className="w-4 h-4"
                          required
                        />
                        <label htmlFor="employer_communicated_swms_yes" className="text-sm cursor-pointer">Yes</label>
                      </div>
                      <div className="flex items-center space-x-2">
                        <input
                          type="radio"
                          id="employer_communicated_swms_no"
                          name="employer_communicated_swms"
                          checked={formData.employer_communicated_swms === false}
                          onChange={() => setFormData(prev => ({ ...prev, employer_communicated_swms: false }))}
                          className="w-4 h-4"
                        />
                        <label htmlFor="employer_communicated_swms_no" className="text-sm cursor-pointer">No</label>
                      </div>
                    </div>
                    {formData.employer_communicated_swms === false && (
                      <Textarea
                        className="mt-2"
                        placeholder="Please explain..."
                        value={formData.employer_communicated_swms_explanation}
                        onChange={(e) => setFormData(prev => ({ ...prev, employer_communicated_swms_explanation: e.target.value }))}
                        required
                      />
                    )}
                  </div>
                </div>
              </div>

              {/* Signature */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold text-slate-800 border-b pb-2">Inductee Signature</h3>
                
                <div className="space-y-2">
                  <Label>Sign below *</Label>
                  <div className="border-2 border-slate-300 rounded-lg bg-white">
                    <SignatureCanvas
                      ref={signatureRef}
                      onEnd={captureInducteeSignature}
                      canvasProps={{
                        className: 'w-full h-40'
                      }}
                    />
                  </div>
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={clearSignature}
                  >
                    Clear Signature
                  </Button>
                  {(formData.first_name || formData.surname) && (
                    <div className="mt-2 p-2 bg-slate-100 rounded border border-slate-300">
                      <p className="text-sm font-medium text-slate-700">
                        Name: {formData.first_name} {formData.surname}
                      </p>
                      <p className="text-sm text-slate-600 mt-1">
                        Date: {new Date(formData.inductee_signature_date).toLocaleDateString("en-AU")}
                      </p>
                    </div>
                  )}
                </div>

                <p className="text-xs text-slate-600 italic">
                  By signing above, I declare that all information provided is truthful, I understand and will adhere to all site safety rules, and I have read and understood my company's SWMS.
                </p>
              </div>

              {/* Site Manager Review & Countersignature */}
              <div className="space-y-3 sm:space-y-4 p-4 sm:p-6 bg-gradient-to-r from-orange-50 to-amber-50 rounded-lg border-2 border-orange-300">
                <div className="flex items-start space-x-2 sm:space-x-3">
                  <AlertCircle className="w-5 h-5 sm:w-6 sm:h-6 text-orange-600 mt-1 flex-shrink-0" />
                  <div className="space-y-3 sm:space-y-4 flex-1">
                    <div>
                      <h3 className="text-base sm:text-lg font-bold text-orange-900 mb-1">
                        Site Manager Review & Countersignature Required
                      </h3>
                      <p className="text-xs sm:text-sm text-orange-800">
                        Before submitting this induction, the <strong>Site Manager must review this form</strong> on this device 
                        and provide their countersignature below. The Site Manager's name is pre-populated from the site allocation. Only the Site Manager can press the Submit button.
                      </p>
                    </div>

                    <div className="space-y-3 sm:space-y-4 bg-white p-3 sm:p-4 rounded-lg border border-orange-200">
                      <div>
                        <Label htmlFor="inductor_name" className="text-base font-semibold">
                          Site Manager Name *
                        </Label>
                        <Input
                          id="inductor_name"
                          value={formData.inductor_name}
                          readOnly
                          className="mt-1 bg-slate-50 cursor-not-allowed"
                        />
                        <p className="text-xs text-slate-500 mt-1 italic">
                          Pre-populated from allocated site manager. This field cannot be edited.
                        </p>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-base font-semibold">Site Manager Countersignature *</Label>
                        <p className="text-xs text-slate-600 mb-2">
                          Site Manager: By signing below, I confirm that I have reviewed this induction form with the inductee 
                          and authorize their work on site.
                        </p>
                        <div className="border-2 border-orange-400 rounded-lg bg-white">
                          <SignatureCanvas
                            ref={managerSignatureRef}
                            onEnd={captureManagerSignature}
                            canvasProps={{
                              className: 'w-full h-40'
                            }}
                          />
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="sm"
                          onClick={clearManagerSignature}
                        >
                          Clear Manager Signature
                        </Button>
                        <div className="mt-2 p-2 bg-orange-50 rounded border border-orange-300">
                          <p className="text-sm text-slate-600">
                            Date: {new Date(formData.inductor_signature_date).toLocaleDateString("en-AU")}
                          </p>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Submit */}
              <div className="flex space-x-2">
                <Button
                  type="submit"
                  className="flex-1 bg-gradient-to-r from-orange-600 to-red-600 hover:from-orange-700 hover:to-red-700 text-lg py-6 font-bold"
                  disabled={loading}
                >
                  {loading ? "Submitting..." : "Site Manager: Submit Induction"}
                </Button>
              </div>
              <p className="text-xs text-center text-slate-500 italic">
                ⚠️ Only the Site Manager should press the Submit button after countersigning
              </p>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
